import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ArrowLeft, Download, DollarSign, AlertTriangle, TrendingUp, Receipt } from 'lucide-react';
import { useThemeStore } from '../../stores/theme.store';
import { useFeesMembershipReport, useExportReport } from '../../hooks/useFinancialReports';
import type { FeesMembershipReportFilters } from '../../hooks/useFinancialReports';

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#a855f7'];

function fmtBRL(centavos: number | undefined | null): string {
  if (centavos === null || centavos === undefined) return 'R$ 0,00';
  return `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function FeesMembershipReportPage() {
  const navigate = useNavigate();
  const { isDarkMode } = useThemeStore();
  const [competence, setCompetence] = useState('');
  const [chargeStatus, setChargeStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMutation = useExportReport();

  const filters: FeesMembershipReportFilters = useMemo(() => ({
    ...(competence ? { competence } : {}),
    ...(chargeStatus ? { chargeStatus } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  }), [competence, chargeStatus, startDate, endDate]);

  const { data, isLoading } = useFeesMembershipReport(filters);

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  const summary = data?.summary;
  const delinquencyHigh = (summary?.delinquencyRate ?? 0) > 10;

  const handleExport = (format: 'PDF' | 'CSV' | 'XLSX') => {
    setShowExportMenu(false);
    exportMutation.mutate({ reportType: 'fees-membership', format, filters });
  };

  // Bar chart data: membership paid/pending/failed per competence
  const barChartData = useMemo(() => {
    if (!data?.membershipByCompetence) return [];
    return data.membershipByCompetence.map((entry) => ({
      competence: entry.competence,
      Pagas: entry.paidAmount / 100,
      Pendentes: entry.pendingAmount / 100,
      Falhas: entry.failedAmount / 100,
    }));
  }, [data?.membershipByCompetence]);

  // Pie chart data: revenue distribution by fee type
  const pieChartData = useMemo(() => {
    if (!data?.feesByType) return [];
    return data.feesByType.map((entry) => ({
      name: entry.type,
      value: entry.totalRevenue / 100,
    }));
  }, [data?.feesByType]);

  // Skeleton loader
  if (isLoading) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="h-8 w-72 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`${cardBg} rounded-xl p-5 space-y-3`}>
                <div className="h-4 w-24 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
                <div className="h-7 w-36 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
              </div>
            ))}
          </div>
          <div className={`${cardBg} rounded-xl p-6 h-72 animate-pulse`} />
          <div className={`${cardBg} rounded-xl p-6 h-72 animate-pulse`} />
        </div>
      </div>
    );
  }

  // Empty state
  const hasData = data && (
    (summary?.totalMembershipCollected ?? 0) > 0 ||
    (summary?.totalMembershipPending ?? 0) > 0 ||
    (summary?.transactionFeeRevenue ?? 0) > 0 ||
    (data.membershipByCompetence && data.membershipByCompetence.length > 0)
  );

  if (!hasData) {
    return (
      <div className={`min-h-screen ${bg} p-6`}>
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate('/admin/reports')} className={`flex items-center gap-2 mb-6 ${textSecondary} hover:${textPrimary} transition-colors`}>
            <ArrowLeft className="w-4 h-4" /> Voltar
          </button>
          <div className={`${cardBg} rounded-xl p-12 text-center`}>
            <Receipt className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
            <h2 className={`text-lg font-semibold ${textPrimary}`}>Nenhum dado encontrado</h2>
            <p className={`mt-2 ${textSecondary}`}>Não há dados de taxas e mensalidades para os filtros selecionados. Ajuste os filtros e tente novamente.</p>
          </div>
        </div>
      </div>
    );
  }


  const kpiCards = [
    {
      label: 'Mensalidades Arrecadadas',
      value: fmtBRL(summary?.totalMembershipCollected),
      color: 'text-green-500',
      icon: DollarSign,
      iconBg: 'bg-green-500/10',
    },
    {
      label: 'Mensalidades Pendentes',
      value: fmtBRL(summary?.totalMembershipPending),
      color: 'text-yellow-500',
      icon: AlertTriangle,
      iconBg: 'bg-yellow-500/10',
    },
    {
      label: 'Taxa de Inadimplência',
      value: `${(summary?.delinquencyRate ?? 0).toFixed(2)}%`,
      color: delinquencyHigh ? 'text-red-500' : 'text-yellow-500',
      icon: AlertTriangle,
      iconBg: delinquencyHigh ? 'bg-red-500/10' : 'bg-yellow-500/10',
    },
    {
      label: 'Receita de Taxas',
      value: fmtBRL(summary?.transactionFeeRevenue),
      color: 'text-blue-500',
      icon: Receipt,
      iconBg: 'bg-blue-500/10',
    },
    {
      label: 'Projeção Mensal',
      value: fmtBRL(summary?.projectedMonthlyRevenue),
      color: 'text-purple-500',
      icon: TrendingUp,
      iconBg: 'bg-purple-500/10',
    },
  ];

  const projection = data?.projection;

  return (
    <div className={`min-h-screen ${bg} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/reports')} className={`p-2 rounded-lg ${cardBg} border ${border} hover:opacity-80 transition-opacity`}>
              <ArrowLeft className={`w-4 h-4 ${textPrimary}`} />
            </button>
            <div>
              <h1 className={`text-2xl font-bold ${textPrimary}`}>Relatório de Taxas e Mensalidades</h1>
              <p className={`text-sm ${textSecondary}`}>Arrecadação, inadimplência e projeção de receita</p>
            </div>
          </div>
          {/* Export button */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              {exportMutation.isPending ? 'Exportando...' : 'Exportar'}
            </button>
            {showExportMenu && (
              <div className={`absolute right-0 mt-2 w-40 rounded-lg shadow-lg border ${border} ${cardBg} z-10`}>
                {(['PDF', 'CSV', 'XLSX'] as const).map((fmt) => (
                  <button key={fmt} onClick={() => handleExport(fmt)} className={`block w-full text-left px-4 py-2 text-sm ${textPrimary} hover:bg-blue-500/10 first:rounded-t-lg last:rounded-b-lg`}>
                    {fmt === 'XLSX' ? 'Excel' : fmt}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className={`${cardBg} rounded-xl p-4 border ${border}`}>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Competência</label>
              <input
                type="month"
                value={competence}
                onChange={(e) => setCompetence(e.target.value)}
                className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}
                placeholder="YYYY-MM"
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Status da Cobrança</label>
              <select value={chargeStatus} onChange={(e) => setChargeStatus(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`}>
                <option value="">Todos</option>
                <option value="PAID">Pago</option>
                <option value="PENDING">Pendente</option>
                <option value="FAILED">Falha</option>
              </select>
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Início (Taxas)</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1 ${textSecondary}`}>Fim (Taxas)</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${inputBg}`} />
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {kpiCards.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className={`${cardBg} rounded-xl p-5 border ${border}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className={`p-2 rounded-lg ${kpi.iconBg}`}>
                    <Icon className={`w-5 h-5 ${kpi.color}`} />
                  </div>
                  <span className={`text-sm font-medium ${textSecondary}`}>{kpi.label}</span>
                </div>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </div>
            );
          })}
        </div>


        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Grouped Bar Chart - Membership by Competence */}
          {barChartData.length > 0 && (
            <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
              <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Mensalidades por Competência</h2>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={barChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
                  <XAxis dataKey="competence" tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280', fontSize: 12 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: isDarkMode ? '#1f2937' : '#fff', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`, borderRadius: 8 }}
                    labelStyle={{ color: isDarkMode ? '#fff' : '#111827' }}
                    formatter={(value: number | undefined) => value != null ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                  />
                  <Legend />
                  <Bar dataKey="Pagas" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Pendentes" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Falhas" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Pie Chart - Revenue Distribution by Fee Type */}
          {pieChartData.length > 0 && (
            <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
              <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Distribuição de Receita por Tipo de Taxa</h2>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={110}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }: { name?: string; percent?: number }) => `${name ?? ''} ${((percent ?? 0) * 100).toFixed(1)}%`}
                  >
                    {pieChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: isDarkMode ? '#1f2937' : '#fff', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`, borderRadius: 8 }}
                    formatter={(value: number | undefined) => value != null ? `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : ''}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Membership Table by Competence */}
        {data?.membershipByCompetence && data.membershipByCompetence.length > 0 && (
          <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
            <h2 className={`text-lg font-semibold mb-4 ${textPrimary}`}>Mensalidades por Competência</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${border}`}>
                    <th className={`text-left py-3 px-4 font-medium ${textSecondary}`}>Competência</th>
                    <th className={`text-right py-3 px-4 font-medium ${textSecondary}`}>Contas Cobradas</th>
                    <th className={`text-right py-3 px-4 font-medium ${textSecondary}`}>Pendentes</th>
                    <th className={`text-right py-3 px-4 font-medium ${textSecondary}`}>Falhas</th>
                    <th className={`text-right py-3 px-4 font-medium ${textSecondary}`}>Valor Arrecadado</th>
                    <th className={`text-right py-3 px-4 font-medium ${textSecondary}`}>Valor Pendente</th>
                    <th className={`text-right py-3 px-4 font-medium ${textSecondary}`}>Inadimplência</th>
                  </tr>
                </thead>
                <tbody>
                  {data.membershipByCompetence.map((row) => {
                    const rowDelinquencyHigh = row.delinquencyRate > 10;
                    return (
                      <tr key={row.competence} className={`border-b ${border} hover:${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <td className={`py-3 px-4 font-medium ${textPrimary}`}>{row.competence}</td>
                        <td className={`py-3 px-4 text-right ${textPrimary}`}>{row.paidCount}</td>
                        <td className={`py-3 px-4 text-right text-yellow-500`}>{row.pendingCount}</td>
                        <td className={`py-3 px-4 text-right text-red-500`}>{row.failedCount}</td>
                        <td className={`py-3 px-4 text-right text-green-500 font-medium`}>{fmtBRL(row.paidAmount)}</td>
                        <td className={`py-3 px-4 text-right text-yellow-500`}>{fmtBRL(row.pendingAmount)}</td>
                        <td className={`py-3 px-4 text-right font-medium ${rowDelinquencyHigh ? 'text-red-500' : 'text-yellow-500'}`}>
                          {row.delinquencyRate.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* Projection Card */}
        {projection && (
          <div className={`${cardBg} rounded-xl p-6 border ${border}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <TrendingUp className="w-5 h-5 text-purple-500" />
              </div>
              <h2 className={`text-lg font-semibold ${textPrimary}`}>Projeção de Receita Mensal</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Contas Ativas</p>
                <p className={`text-lg font-bold ${textPrimary} mt-1`}>{projection.activeAccounts}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Valor da Mensalidade</p>
                <p className={`text-lg font-bold ${textPrimary} mt-1`}>{fmtBRL(projection.membershipValue)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Projeção Mensalidades</p>
                <p className={`text-lg font-bold text-green-500 mt-1`}>{fmtBRL(projection.projectedMembership)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Média Diária de Taxas</p>
                <p className={`text-lg font-bold ${textPrimary} mt-1`}>{fmtBRL(projection.avgDailyFees)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border}`}>
                <p className={`text-sm ${textSecondary}`}>Projeção Taxas (30 dias)</p>
                <p className={`text-lg font-bold text-blue-500 mt-1`}>{fmtBRL(projection.projectedFees)}</p>
              </div>
              <div className={`p-4 rounded-lg border ${border} bg-purple-500/5`}>
                <p className={`text-sm ${textSecondary}`}>Total Projetado</p>
                <p className="text-lg font-bold text-purple-500 mt-1">{fmtBRL(projection.totalProjected)}</p>
              </div>
            </div>
            <p className={`text-xs mt-4 ${textSecondary}`}>
              Base de cálculo: ({projection.activeAccounts} contas × {fmtBRL(projection.membershipValue)}) + ({fmtBRL(projection.avgDailyFees)}/dia × 30 dias)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}